import type { PetApi } from "../api/types";

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * Live XP progress within the current level, interpolated from the time to the
 * next passive +1 XP tick. `nextXpAt` marks the next tick, so the current
 * interval began at `nextXpAt - xpIntervalMs`; the elapsed fraction of that
 * interval is the sub-tick XP the pet has "earned" but not yet banked. A dead
 * pet (no `nextXpAt`) freezes at its whole-XP progress.
 */
export function xpProgress(
  pet: Pick<PetApi, "xpIntoLevel" | "xpForNext" | "nextXpAt" | "xpIntervalMs" | "dead">,
  now: number = Date.now()
): { fillFraction: number; currentXp: number } {
  const tick =
    pet.dead || pet.nextXpAt == null || pet.xpIntervalMs <= 0
      ? 0
      : clamp(1 - (pet.nextXpAt - now) / pet.xpIntervalMs, 0, 1);
  // The sub-tick can push into-level XP past the level target (a whole tick is
  // banked as a level-up server-side); cap the readout so it never reads e.g.
  // "9.4 / 9". The bar sits full until the next poll banks the XP.
  const currentXp = pet.xpForNext > 0 ? Math.min(pet.xpIntoLevel + tick, pet.xpForNext) : pet.xpIntoLevel + tick;
  const fillFraction = pet.xpForNext > 0 ? clamp(currentXp / pet.xpForNext, 0, 1) : 0;
  return { fillFraction, currentXp };
}
